import { Typography } from "@mui/material";
import DocumentCard from "@components/DocumentCard/DocumentCard";
import Paper from "@components/Paper/Paper";
import PaperItem from "@components/Paper/PaperItem";
import { useDownloadCertificate } from "@pages/vessel-management/vessel-status/hooks/useDownloadCertificate";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import type { DepartureMarineInformation } from "@services/departure/types";
import { convertUtcToUae, formatDateToDDMMYYYY, toDate } from "@utils/date-time";
import { checkPermission } from "@utils/user-permissions";
import { useTranslation } from "react-i18next";

const LANG_BASE = "vessel-call.departure-status-page";
const TEST_ID = "departure-marine-information";
const FIELD_LABEL_CLASS = "w-36 shrink-0 font-normal text-[#646A77]";
const VALUE_CLASS = "min-w-0 flex-1 font-medium";

type Props = {
  marineInformation?: DepartureMarineInformation | null;
};

const MarineInformation = ({ marineInformation }: Props) => {
  const { t } = useTranslation();
  const { userAccessPermissions, isShippingAgentUser } = useAuthStore();
  const { handleDownload, handlePreview } = useDownloadCertificate();
  const canViewInternal =
    checkPermission(userAccessPermissions, "hasInternalNotesAccess") && !isShippingAgentUser;

  if ((!canViewInternal && !isShippingAgentUser) || !marineInformation) return null;

  const approvalDate = toDate(marineInformation.approvedAt);
  const formattedApprovalDate = approvalDate
    ? formatDateToDDMMYYYY(convertUtcToUae(approvalDate.toISOString()))
    : undefined;

  const groups = [
    {
      key: "external",
      notesLabel: "notes-to-agent",
      notes: marineInformation.externalNotes,
      attachmentsKey: "attachments",
      url: marineInformation.externalAttachmentsUrl,
      fileName: marineInformation.externalAttachmentsName,
    },
    ...(canViewInternal
      ? [
          {
            key: "internal",
            notesLabel: "internal-notes",
            notes: marineInformation.internalNotes,
            attachmentsKey: "internal-attachments",
            url: marineInformation.internalAttachmentsUrl,
            fileName: marineInformation.internalAttachmentsName,
          },
        ]
      : []),
  ];
  const fields = groups.flatMap((group) => {
    if (!group.notes?.trim() && !group.url?.trim()) return [];

    return [
      { key: `${group.key}-notes`, label: group.notesLabel, text: group.notes },
      { key: `${group.key}-date`, label: "approval-date", text: formattedApprovalDate },
      {
        key: group.attachmentsKey,
        label: group.attachmentsKey,
        url: group.url,
        fileName: group.fileName,
      },
    ].filter((field) => field.text?.trim() || field.url?.trim());
  });

  if (!fields.length) return null;

  return (
    <section className="mt-6" data-testid={TEST_ID}>
      <Paper variant="outlined" sx={{ typography: "body1" }}>
        <div className="flex flex-col gap-3">
          <Typography
            component="h2"
            variant="subtitle1"
            className="font-bold"
            sx={{ fontWeight: 700 }}
          >
            {t(`${LANG_BASE}.marine-information`)}
          </Typography>
          <Paper variant="outlined" sx={{ typography: "body1" }}>
            <div className="flex flex-col gap-3" data-testid={`${TEST_ID}-approval`}>
              <Typography
                component="h3"
                variant="subtitle1"
                className="font-bold"
                sx={{ fontWeight: 700 }}
              >
                {t(`${LANG_BASE}.departure-approval`)}
              </Typography>
              <div className="flex flex-col gap-4">
                {fields.map((field, index) => (
                  <div
                    key={field.key}
                    data-testid={`${TEST_ID}-${field.key}`}
                    className={index > 0 ? "border-t border-[#F1F2F4] pt-3" : undefined}
                  >
                    {field.url ? (
                      <Typography
                        component="div"
                        variant="body1"
                        className="min-w-0 grid grid-cols-2 gap-4"
                      >
                        <DocumentCard
                          title={t(`${LANG_BASE}.${field.label}`)}
                          fileName={field.fileName || "-"}
                          onPreview={() => handlePreview("document", "bff-vessel-call", field.url!)}
                          onDownload={() =>
                            handleDownload(
                              "document",
                              "bff-vessel-call",
                              field.url!,
                              field.fileName ?? undefined,
                            )
                          }
                        />
                      </Typography>
                    ) : (
                      <PaperItem
                        testId={`${TEST_ID}-${field.key}-content`}
                        translate={t(`${LANG_BASE}.${field.label}`)}
                        text={field.text}
                        textStyles={VALUE_CLASS}
                        tranStyle={FIELD_LABEL_CLASS}
                        wrapperStyle="min-w-0 gap-4 pt-3"
                        labelClassName={FIELD_LABEL_CLASS}
                        valueClassName={VALUE_CLASS}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </Paper>
        </div>
      </Paper>
    </section>
  );
};

export default MarineInformation;
